import { create } from "zustand";
import { State } from "@progress/kendo-data-query";
import { loadRuntimeConfig, runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { ExplorerBreadcrumbItem, ExplorerItem, ExplorerSelectedState } from "@presentation/components/helpers/useRemoteFolderExplorer";

// seems like the one loaded in index.tsx is not available here, so we load it again
// eslint-disable-next-line unicorn/no-top-level-side-effects -- must run before the store below reads runtimeConfig
await loadRuntimeConfig();

export type SelectedState = ExplorerSelectedState;

/**
Label of the root breadcrumb crumb, shared with the page so both build it the same way.
*/
export const buildRootBreadcrumbLabel = (locationName: string) => `Location: ${locationName}`;

interface FileExplorerStore {
  rawFiles: ExplorerItem[];
  metaState: State;
  selectedState: ExplorerSelectedState;
  /**
  `sourcePath` is only set once a crumb was reached by disambiguating a colliding folder - it scopes
  every fetch/selection at or below that depth to the one configured raw path it came from, so
  descending further (or re-fetching after an upload) never silently re-merges the collision.
  */
  breadcrumbItems: ExplorerBreadcrumbItem[];
  /**
  The project this store's `breadcrumbItems`/`selectedState` currently belong to - kept in the store
  (not a component-local ref) because Kendo's `TabStripTab` unmounts an inactive tab's content, which
  would otherwise reset a local "last seen project" ref and make a genuine project switch that happened
  while this tab was hidden look like a fresh first load, leaving the previous project's breadcrumb/scope
  in place instead of resetting it.
  */
  lastProjectId: string | null;
  setLastProjectId: (projectId: string) => void;
  resetSelectedGridData: () => void;
  setRawFiles: (items: ExplorerItem[]) => void;
  setSelectedState: (newSelectedState: ExplorerSelectedState) => void;
  setBreadcrumbItems: (items: ExplorerBreadcrumbItem[]) => void;
  setMetaState: (state: State) => void;
}

export const useFileExplorerStore = create<FileExplorerStore>((set) => ({
  rawFiles: [],
  metaState: {
    skip: 0, // start at first page
    take: 50, // default page size
    sort: [{ field: "fileName", dir: "asc" }],
    filter: undefined, // optional, ensures no filter applied initially
  },
  selectedState: {},
  breadcrumbItems: [{ id: "home", text: buildRootBreadcrumbLabel(runtimeConfig.RAWFILE_CONTAINER_NAME), disabled: false }],
  lastProjectId: null,
  setLastProjectId: (projectId) => set({ lastProjectId: projectId }),
  setBreadcrumbItems: (items) => {
    set({ breadcrumbItems: items });
  },
  setRawFiles: (items) => set({ rawFiles: items }),
  setSelectedState: (newSelectedState: SelectedState) => set({ selectedState: newSelectedState }),
  resetSelectedGridData: () => {
    set({ selectedState: {} });
  },
  setMetaState: (state: State) => {
    set({ metaState: state });
  },
}));
